// Which features the landlord view shows, decided by the workspace settings
// that the admin panel manages (Requirements tab). A feature that is switched
// off is shown locked, not hidden.
//
// Every feature has one rule: a function that reads the workspace document and
// says whether the feature is on. Most read `requirements_config.<key>.active`;
// the older ones still live in their own fields (the admin panel keeps those
// fields as the source of truth).
//
// To gate a new feature: add a rule here, then call `isEnabled(FEATURES.X)`.
const fromRequirementsConfig = (key) => (workspace) =>
  workspace?.requirements_config?.[key]?.active === true;

const FEATURE_RULES = {
  // Identity
  KYC: fromRequirementsConfig("kyc"),
  AML: fromRequirementsConfig("aml"),
  // Documents
  BASIC_DOCUMENTS: fromRequirementsConfig("basic_documents"),
  DOCUMENT_FORENSICS: fromRequirementsConfig("document_forensics"),
  // Solvency
  DEFAULTER_CHECK: fromRequirementsConfig("defaulter_check"),
  FINANCIAL_ASSESSMENT: fromRequirementsConfig("financial_assessment"),
  OPEN_BANKING: fromRequirementsConfig("open_banking"),
  SOURCE_OF_FUNDS: (workspace) => workspace?.affidavit_activated === true,
  // Demographics (household, pets, ...)
  APPLICANT_QUESTIONS: (workspace) =>
    workspace?.tenant_requirements?.household?.active === true,
  // Additional guarantees
  INSURABILITY: (workspace) => workspace?.insurance_service_status === true,
  RENT_DEFAULT_INSURANCE: (workspace) =>
    workspace?.skor_protect_service === true,
  GUARANTOR: fromRequirementsConfig("guarantor"),
};

// Names used in the code: FEATURES.AML === "AML"
export const WORKSPACE_FEATURES = Object.fromEntries(
  Object.keys(FEATURE_RULES).map((name) => [name, name])
);

// Only the landlord view follows the workspace settings. Admins see
// everything, and the tenant's own profile is not affected.
const followsWorkspaceConfig = (userType) => userType === "landlord";

export const isWorkspaceFeatureEnabled = ({ workspace, userType, feature }) => {
  if (!followsWorkspaceConfig(userType)) return true;
  return FEATURE_RULES[feature]?.(workspace) === true;
};

// The onboarding steps that follow requirements_config, and their keys in it.
const ONBOARDING_CONFIG_KEYS = {
  KYC: "kyc",
  OPEN_BANKING: "open_banking",
};

// Tenant onboarding: the workspace the tenant was invited by decides which
// steps are asked. With no workspace (direct signup) nothing is restricted.
// A workspace with no requirements_config, or a config without that key, is
// not restricted either. Only a feature explicitly switched off
// (`active: false`) is not asked.
export const isOnboardingFeatureEnabled = ({ workspace, feature }) => {
  if (!workspace || !workspace.requirements_config) return true;
  const configKey = ONBOARDING_CONFIG_KEYS[feature];
  if (!configKey) return true;
  return workspace.requirements_config[configKey]?.active !== false;
};
